import { cn } from "@/lib/utils";
import { num } from "@/lib/grant-scopes";
import { Search } from "lucide-react";
import { type ReactNode, useState } from "react";

const FEW = 10;
const PREVIEW = 8;

/**
 * A list that stays short: past ten items it shows the first eight and a
 * "Show all", and offers a search box. Sources can have hundreds of channels
 * and people, and dozens of skills.
 */
export function Many<T>({
  items,
  render,
  text,
  noun,
  indent,
  find,
  showAll,
}: {
  items: T[];
  /** `query` is what the search box holds, for rows that narrow their own contents to it. */
  render: (t: T, query: string) => ReactNode;
  text: (t: T) => string;
  noun: string;
  indent?: boolean;
  /** Search placeholder, when the items aren't what `noun` counts (a skill set holds many skills). */
  find?: string;
  /** "Show all" label for the rows still hidden, for the same case. */
  showAll?: (hidden: T[]) => string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  if (items.length <= FEW) return <>{items.map((t) => render(t, ""))}</>;
  const q = query.trim().toLowerCase();
  const matching = q ? items.filter((t) => text(t).toLowerCase().includes(q)) : items;
  const shown = q || open ? matching : matching.slice(0, PREVIEW);
  return (
    <>
      <div className={cn("relative px-4 py-2", indent && "pl-[52px]")}>
        <Search className={cn("pointer-events-none absolute top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground", indent ? "left-[62px]" : "left-6")} aria-hidden />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={find ?? `Find in ${num(items.length)} ${noun}`}
          aria-label={`Find ${noun}`}
          className="h-8 w-full rounded-md border border-input bg-background pl-7 pr-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
        />
      </div>
      {shown.map((t) => render(t, q))}
      {q && matching.length === 0 && <Empty text={`No ${noun} match “${query.trim()}”.`} />}
      {!q && matching.length > PREVIEW && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn("px-4 py-2 text-left text-xs font-medium text-muted-foreground hover:text-foreground", indent && "pl-[52px]")}
        >
          {open ? "Show fewer" : showAll ? showAll(matching.slice(PREVIEW)) : `Show all ${num(matching.length)} ${noun}`}
        </button>
      )}
    </>
  );
}


function Empty({ text }: { text: string }) {
  return <p className="px-4 py-2.5 text-xs text-muted-foreground">{text}</p>;
}
